import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Check, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AkunView from "@/components/apik/AkunView";
import EmonevView from "@/components/apik/EmonevView";
import PlaceholderView from "@/components/apik/PlaceholderView";
import MonevModule from "@/components/monev/MonevModule";
import PermohonanView from "@/components/permohonan/PermohonanView";

const MENUS = [
  { id: "akun", label: "Akun" },
  { id: "e-monev", label: "e-Monev" },
  { id: "permohonan", label: "Permohonan Informasi Publik" },
  { id: "emonev", label: "Rekap Program Kegiatan" },
  { id: "datastat", label: "Data dan Statistik" },
];
const TAHUN_OPTS = [2024, 2025, 2026];

export default function Apik() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("akun");
  const [monevYear, setMonevYear] = useState(null);
  const [yearOpen, setYearOpen] = useState(false);

  const pickYear = (y) => {
    setMonevYear(y);
    setActive("e-monev");
    setYearOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#F3F7FB]">
      <header className="sticky top-0 z-40 border-b border-[#E3EBF3] bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <img
              src="/logo.webp"
              alt="Logo"
              className="h-8 w-8 object-contain"
            />
            <div className="leading-tight">
              <p
                data-testid="apik-brand"
                className="text-sm font-bold text-[#0B2440]"
              >
                APIK
                {user?.role === "ppid" && (
                  <>
                    {" "}
                    <span className="ml-1 rounded-full bg-[#E8A33D]/15 px-2 py-0.5 text-[10px] font-bold text-[#B97A18]">
                      ADMIN
                    </span>
                  </>
                )}
              </p>
              <p className="text-[10px] font-medium text-slate-500">
                Administrasi Pelaporan Informasi Publik
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs font-semibold text-[#1479BF] sm:block">
              {user?.label}
            </span>
            <button
              data-testid="apik-logout-button"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="rounded-full border border-[#E3EBF3] px-4 py-1.5 text-xs font-semibold text-slate-600 transition-colors duration-300 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
            >
              Keluar
            </button>
          </div>
        </div>
      </header>

      <nav data-testid="apik-menu" className="mx-auto max-w-5xl px-6 pt-6">
        <div className="flex flex-wrap gap-2">
          {MENUS.map((m) =>
            m.id === "e-monev" ? (
              <div key={m.id} className="relative">
                <button
                  data-testid="apik-tab-e-monev"
                  onClick={() => setYearOpen((o) => !o)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-xs font-bold tracking-[0.08em] transition-colors duration-300 ${
                    active === m.id
                      ? "bg-[#1479BF] text-white"
                      : "border border-[#E3EBF3] bg-white text-slate-600 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                  }`}
                >
                  {monevYear ? `e-Monev ${monevYear}` : m.label}
                  <ChevronDown
                    aria-hidden="true"
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${
                      yearOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {yearOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setYearOpen(false)}
                    />
                    <div
                      data-testid="apik-year-menu"
                      className="absolute left-0 top-full z-40 mt-2 w-44 rounded-2xl border border-[#E3EBF3] bg-white p-2 shadow-xl"
                    >
                      <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                        Pilih Tahun
                      </p>
                      {TAHUN_OPTS.map((y) => (
                        <button
                          key={y}
                          data-testid={`apik-year-option-${y}`}
                          onClick={() => pickYear(y)}
                          className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold transition-colors duration-150 ${
                            monevYear === y
                              ? "bg-[#EFF6FC] text-[#1479BF]"
                              : "text-slate-600 hover:bg-[#F3F7FB]"
                          }`}
                        >
                          {y}
                          {monevYear === y && (
                            <Check aria-hidden="true" className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                key={m.id}
                data-testid={`apik-tab-${m.id}`}
                onClick={() => setActive(m.id)}
                className={`rounded-full px-5 py-2.5 text-xs font-bold tracking-[0.08em] transition-colors duration-300 ${
                  active === m.id
                    ? "bg-[#1479BF] text-white"
                    : "border border-[#E3EBF3] bg-white text-slate-600 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                }`}
              >
                {m.label}
              </button>
            )
          )}
        </div>
      </nav>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {active === "akun" ? (
          <AkunView user={user} />
        ) : active === "e-monev" ? (
          <MonevModule user={user} year={monevYear ?? 2026} />
        ) : active === "permohonan" ? (
          <PermohonanView user={user} />
        ) : active === "emonev" ? (
          <EmonevView user={user} />
        ) : (
          <PlaceholderView
            title="Data dan Statistik"
            description="Halaman pengelolaan data dan statistik — sedang dalam pengembangan dan akan tersedia pada pembaruan berikutnya."
            icon={BarChart3}
            emptyText="Modul Data dan Statistik sedang dibangun. Silakan kembali lagi pada pembaruan berikutnya."
          />
        )}
      </main>
    </div>
  );
}